const MATRIX_TEXT_ATTRIBUTE = "data-matrix-text";
const MATRIX_TRIGGER_SELECTOR =
  "button, a, [role='button'], input[type='button'], input[type='submit']";
const DEFAULT_DURATION = 500;
const DEFAULT_CHARACTER_SET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const DEFAULT_RESPECT_REDUCED_MOTION = true;

/** @type {WeakMap<HTMLElement, number>} */
const activeAnimations = new WeakMap();

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
);

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function hasOnlyTextNodes(element) {
  return [...element.childNodes].every(
    (node) => node.nodeType === Node.TEXT_NODE,
  );
}

/**
 * @param {HTMLElement} element
 * @returns {string}
 */
function getOriginalText(element) {
  const current = element.dataset.matrixOriginalText;
  if (current !== undefined) return current;

  const originalText = element.textContent ?? "";
  if (!originalText.trim()) return "";

  element.dataset.matrixOriginalText = originalText;

  return originalText;
}

/**
 * @param {HTMLElement} element
 * @returns {number}
 */
function getDuration(element) {
  const rawDuration = Number.parseInt(element.dataset.matrixDuration || "", 10);
  if (!Number.isNaN(rawDuration) && rawDuration > 0) return rawDuration;

  const globalDuration = Number.parseInt(
    document.body?.dataset.matrixDuration || "",
    10,
  );
  if (!Number.isNaN(globalDuration) && globalDuration > 0)
    return globalDuration;

  return DEFAULT_DURATION;
}

/**
 * @param {HTMLElement} element
 * @returns {string}
 */
function getCharacterSet(element) {
  const customSet = element.dataset.matrixCharset?.trim() || "";
  if (customSet.length) return customSet;

  const globalSet = document.body?.dataset.matrixCharset?.trim() || "";
  if (globalSet.length) return globalSet;

  return DEFAULT_CHARACTER_SET;
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function shouldRespectReducedMotion(element) {
  const elementValue = element.dataset.matrixRespectReducedMotion;
  if (elementValue === "true") return true;
  if (elementValue === "false") return false;

  const globalValue = document.body?.dataset.matrixRespectReducedMotion;
  if (globalValue === "true") return true;
  if (globalValue === "false") return false;

  return DEFAULT_RESPECT_REDUCED_MOTION;
}

/**
 * @param {string} text
 * @param {number} revealCount
 * @param {string} characterSet
 * @returns {string}
 */
function getScrambledText(text, revealCount, characterSet) {
  let output = "";

  for (let index = 0; index < text.length; index += 1) {
    const character = text.charAt(index);

    if (/\s/.test(character) || index < revealCount) {
      output += character;
      continue;
    }

    const randomIndex = Math.floor(Math.random() * characterSet.length);
    output += characterSet[randomIndex] || character;
  }

  return output;
}

/**
 * @param {HTMLElement} element
 * @param {boolean} [restoreOriginal]
 */
function stopMatrixAnimation(element, restoreOriginal = true) {
  const frameId = activeAnimations.get(element);
  if (frameId !== undefined) {
    cancelAnimationFrame(frameId);
    activeAnimations.delete(element);
  }

  if (restoreOriginal) {
    const originalText = getOriginalText(element);
    element.textContent = originalText;
  }
}

/**
 * @param {HTMLElement} element
 */
function startMatrixAnimation(element) {
  if (
    (shouldRespectReducedMotion(element) && prefersReducedMotion.matches) ||
    !hasOnlyTextNodes(element)
  ) {
    return;
  }

  const originalText = getOriginalText(element);
  if (!originalText) return;

  stopMatrixAnimation(element, false);

  const duration = getDuration(element);
  const characterSet = getCharacterSet(element);
  const startTime = performance.now();

  /** @param {DOMHighResTimeStamp} currentTime */
  const tick = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const revealCount = Math.floor(progress * originalText.length);

    element.textContent = getScrambledText(
      originalText,
      revealCount,
      characterSet,
    );

    if (progress >= 1) {
      element.textContent = originalText;
      activeAnimations.delete(element);
      return;
    }

    const frameId = requestAnimationFrame(tick);
    activeAnimations.set(element, frameId);
  };

  const frameId = requestAnimationFrame(tick);
  activeAnimations.set(element, frameId);
}

/**
 * @param {ParentNode} node
 * @returns {HTMLElement | null}
 */
function getChildMatrixTarget(node) {
  const matrixTarget = node.querySelector(`[${MATRIX_TEXT_ATTRIBUTE}]`);
  if (!(matrixTarget instanceof HTMLElement)) return null;

  return matrixTarget;
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function getMatrixTarget(target) {
  if (!(target instanceof Element)) return null;

  const trigger = target.closest(MATRIX_TRIGGER_SELECTOR);
  if (!trigger) return null;

  const directTarget = target.closest(`[${MATRIX_TEXT_ATTRIBUTE}]`);
  if (
    directTarget instanceof HTMLElement &&
    directTarget.closest(MATRIX_TRIGGER_SELECTOR) === trigger
  ) {
    return directTarget;
  }

  const matrixTarget = getChildMatrixTarget(trigger);
  if (matrixTarget) return matrixTarget;

  return null;
}

/**
 * @param {HTMLElement} matrixTarget
 * @param {EventTarget | null} relatedTarget
 * @returns {boolean}
 */
function isSameMatrixContext(matrixTarget, relatedTarget) {
  const relatedMatrixTarget = getMatrixTarget(relatedTarget);
  return relatedMatrixTarget === matrixTarget;
}

/**
 * @param {PointerEvent} event
 */
function handlePointerOver(event) {
  if (event.pointerType === "touch") return;

  const matrixTarget = getMatrixTarget(event.target);
  if (!matrixTarget) return;

  if (isSameMatrixContext(matrixTarget, event.relatedTarget)) return;

  startMatrixAnimation(matrixTarget);
}

/**
 * @param {PointerEvent} event
 */
function handlePointerOut(event) {
  const matrixTarget = getMatrixTarget(event.target);
  if (!matrixTarget) return;

  if (isSameMatrixContext(matrixTarget, event.relatedTarget)) return;

  stopMatrixAnimation(matrixTarget);
}

/**
 * @param {FocusEvent} event
 */
function handleFocusIn(event) {
  const matrixTarget = getMatrixTarget(event.target);
  if (!matrixTarget) return;

  startMatrixAnimation(matrixTarget);
}

/**
 * @param {FocusEvent} event
 */
function handleFocusOut(event) {
  const matrixTarget = getMatrixTarget(event.target);
  if (!matrixTarget) return;

  stopMatrixAnimation(matrixTarget);
}

document.addEventListener("pointerover", handlePointerOver);
document.addEventListener("pointerout", handlePointerOut);
document.addEventListener("focusin", handleFocusIn);
document.addEventListener("focusout", handleFocusOut);
